import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AdminAuthService } from '../../../core/auth/admin-auth.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent {
  loginForm: FormGroup;
  loading = false;
  returnUrl: string = '/';

  constructor(
    private fb: FormBuilder,
    private authService: AdminAuthService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    // Si ya está autenticado, redirigir al dashboard
    if (this.authService.isAuthenticated()) {
      this.router.navigate(['/']);
    }

    // Obtener URL de retorno
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';

    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]]
    });
  }

  get email() {
    return this.loginForm.get('email');
  }

  get password() {
    return this.loginForm.get('password');
  }

  onSubmit(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    const { email, password } = this.loginForm.value;

    this.authService.login(email, password).subscribe({
      next: (response) => {
        this.loading = false;
        
        Swal.fire({
          title: '¡Bienvenido!',
          text: `Hola, ${response.data.user.displayName}`,
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });

        setTimeout(() => {
          this.router.navigate([this.returnUrl]);
        }, 500);
      },
      error: (error) => {
        this.loading = false;
        console.error('Login error:', error);

        // Si el email no está verificado, mostrar modal de verificación
        if (error.error?.code === 'EMAIL_NOT_VERIFIED') {
          this.showVerificationModal(email);
          return;
        }

        let errorMessage = 'Error al iniciar sesión. Por favor, intenta nuevamente.';
        
        if (error.error?.message) {
          errorMessage = error.error.message;
        } else if (error.status === 401) {
          errorMessage = 'Credenciales inválidas. Verifica tu email y contraseña.';
        } else if (error.status === 403) {
          errorMessage = 'Acceso denegado. No tienes permisos administrativos.';
        } else if (error.status === 0) {
          errorMessage = 'No se pudo conectar con el servidor. Verifica tu conexión.';
        }

        Swal.fire({
          title: 'Error',
          text: errorMessage,
          icon: 'error',
          confirmButtonColor: '#dc3545'
        });
      }
    });
  }

  /**
   * Manejar clic en "Olvidé mi contraseña"
   */
  onForgotPassword(event: Event): void {
    event.preventDefault();
    
    Swal.fire({
      title: 'Recuperar Contraseña',
      html: '<input id="swal-email" class="form-control" placeholder="Ingresa tu email">',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Enviar Código',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1E2A38',
      preConfirm: () => {
        const email = (document.getElementById('swal-email') as HTMLInputElement).value.trim();
        if (!email) {
          Swal.showValidationMessage('Debes ingresar un email');
          return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          Swal.showValidationMessage('Email inválido');
          return false;
        }
        return email;
      },
      showLoaderOnConfirm: true
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        const email = result.value;
        
        // Llamar al servicio
        this.authService.forgotPassword(email).subscribe({
          next: () => {
            // Mostrar modal para ingresar código
            this.showResetPasswordModal(email);
          },
          error: (error) => {
            Swal.fire({
              title: 'Email Enviado',
              text: 'Si el email está registrado, recibirás un código de recuperación.',
              icon: 'info',
              confirmButtonColor: '#1E2A38'
            });
          }
        });
      }
    });
  }

  /**
   * Mostrar modal para resetear contraseña con código
   */
  private showResetPasswordModal(email: string): void {
    Swal.fire({
      title: 'Código Enviado',
      html: `
        <p>Te hemos enviado un código de 6 dígitos a <strong>${email}</strong></p>
        <div style="text-align: left; margin-top: 20px;">
          <div class="mb-3">
            <label class="form-label">Código de Verificación</label>
            <input id="swal-code" type="text" class="form-control" placeholder="123456" maxlength="6">
          </div>
          <div class="mb-3">
            <label class="form-label">Nueva Contraseña</label>
            <input id="swal-newPassword" type="password" class="form-control" placeholder="Mínimo 8 caracteres">
          </div>
          <div class="mb-3">
            <label class="form-label">Confirmar Contraseña</label>
            <input id="swal-confirmPassword" type="password" class="form-control">
          </div>
        </div>
      `,
      width: '500px',
      showCancelButton: true,
      confirmButtonText: 'Restablecer Contraseña',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1E2A38',
      preConfirm: () => {
        const code = (document.getElementById('swal-code') as HTMLInputElement).value.trim();
        const newPassword = (document.getElementById('swal-newPassword') as HTMLInputElement).value;
        const confirmPassword = (document.getElementById('swal-confirmPassword') as HTMLInputElement).value;

        if (!code || !newPassword || !confirmPassword) {
          Swal.showValidationMessage('Todos los campos son requeridos');
          return false;
        }

        if (code.length !== 6) {
          Swal.showValidationMessage('El código debe tener 6 dígitos');
          return false;
        }

        if (newPassword.length < 8) {
          Swal.showValidationMessage('La contraseña debe tener al menos 8 caracteres');
          return false;
        }

        if (newPassword !== confirmPassword) {
          Swal.showValidationMessage('Las contraseñas no coinciden');
          return false;
        }

        return { code, newPassword };
      },
      showLoaderOnConfirm: true
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        this.authService.resetPassword(email, result.value.code, result.value.newPassword).subscribe({
          next: () => {
            Swal.fire({
              title: '¡Contraseña Restablecida!',
              text: 'Tu contraseña ha sido actualizada correctamente. Ya puedes iniciar sesión.',
              icon: 'success',
              confirmButtonColor: '#1E2A38'
            }).then(() => {
              this.loginForm.patchValue({ email });
            });
          },
          error: (error) => {
            Swal.fire({
              title: 'Error',
              text: error.error?.message || 'Código inválido o expirado',
              icon: 'error',
              confirmButtonColor: '#dc3545'
            }).then(() => {
              // Volver a mostrar el modal
              this.showResetPasswordModal(email);
            });
          }
        });
      }
    });
  }

  /**
   * Manejar clic en "Crear cuenta"
   */
  onRegister(event: Event): void {
    event.preventDefault();
    
    Swal.fire({
      title: 'Crear Cuenta de Administrador',
      html: `
        <div style="text-align: left;">
          <div class="mb-3">
            <label class="form-label">Nombre</label>
            <input id="swal-firstName" type="text" class="form-control" placeholder="Juan">
          </div>
          <div class="mb-3">
            <label class="form-label">Apellido</label>
            <input id="swal-lastName" type="text" class="form-control" placeholder="Pérez">
          </div>
          <div class="mb-3">
            <label class="form-label">Email</label>
            <input id="swal-email" type="email" class="form-control" placeholder="admin@empresa.com">
          </div>
          <div class="mb-3">
            <label class="form-label">Contraseña</label>
            <input id="swal-password" type="password" class="form-control" placeholder="Mínimo 8 caracteres">
          </div>
          <div class="mb-3">
            <label class="form-label">Confirmar Contraseña</label>
            <input id="swal-confirmPassword" type="password" class="form-control" placeholder="Repite la contraseña">
          </div>
        </div>
      `,
      width: '500px',
      showCancelButton: true,
      confirmButtonText: 'Crear Cuenta',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1E2A38',
      preConfirm: () => {
        const firstName = (document.getElementById('swal-firstName') as HTMLInputElement).value.trim();
        const lastName = (document.getElementById('swal-lastName') as HTMLInputElement).value.trim();
        const email = (document.getElementById('swal-email') as HTMLInputElement).value.trim();
        const password = (document.getElementById('swal-password') as HTMLInputElement).value;
        const confirmPassword = (document.getElementById('swal-confirmPassword') as HTMLInputElement).value;

        if (!firstName || !lastName || !email || !password || !confirmPassword) {
          Swal.showValidationMessage('Todos los campos son requeridos');
          return false;
        }

        if (password.length < 8) {
          Swal.showValidationMessage('La contraseña debe tener al menos 8 caracteres');
          return false;
        }

        if (password !== confirmPassword) {
          Swal.showValidationMessage('Las contraseñas no coinciden');
          return false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          Swal.showValidationMessage('Email inválido');
          return false;
        }

        return { firstName, lastName, email, password };
      },
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading()
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        // Llamar al servicio de registro
        this.authService.register(result.value).subscribe({
          next: (response) => {
            // Mostrar modal de verificación de email
            this.showVerificationModal(result.value.email);
          },
          error: (error) => {
            console.error('Register error:', error);
            
            let errorMessage = 'Error al crear la cuenta. Por favor, intenta nuevamente.';
            
            if (error.error?.message) {
              errorMessage = error.error.message;
            } else if (error.status === 400) {
              errorMessage = error.error?.message || 'Datos inválidos. Verifica la información ingresada.';
            }

            Swal.fire({
              title: 'Error al Registrar',
              text: errorMessage,
              icon: 'error',
              confirmButtonColor: '#dc3545'
            });
          }
        });
      }
    });
  }

  /**
   * Mostrar modal de verificación de email
   */
  private showVerificationModal(email: string): void {
    Swal.fire({
      title: 'Verifica tu Email',
      html: `
        <p>Te hemos enviado un código de 4 dígitos a <strong>${email}</strong></p>
        <p class="text-muted">Revisa tu bandeja de entrada y spam.</p>
        <div style="text-align: left; margin-top: 20px;">
          <div class="mb-3">
            <label class="form-label">Código de Verificación</label>
            <input id="swal-code" type="text" class="form-control text-center" placeholder="1234" maxlength="4" style="font-size: 24px; letter-spacing: 10px;">
          </div>
        </div>
        <div style="margin-top: 15px;">
          <a href="#" id="resend-btn" class="btn btn-link btn-sm" style="text-decoration: none; cursor: pointer;">
            ¿No recibiste el código? Reenviar
          </a>
        </div>
      `,
      width: '500px',
      showCancelButton: true,
      confirmButtonText: 'Verificar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1E2A38',
      didOpen: () => {
        // Agregar evento al botón de reenviar
        const resendBtn = document.getElementById('resend-btn');
        if (resendBtn) {
          resendBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.resendVerificationCode(email);
          });
        }
      },
      preConfirm: () => {
        const code = (document.getElementById('swal-code') as HTMLInputElement).value.trim();

        if (!code) {
          Swal.showValidationMessage('Debes ingresar el código');
          return false;
        }

        if (code.length !== 4) {
          Swal.showValidationMessage('El código debe tener 4 dígitos');
          return false;
        }

        return code;
      },
      showLoaderOnConfirm: true,
      allowOutsideClick: false
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        // Verificar el código
        this.authService.verifyEmail(email, result.value).subscribe({
          next: (response) => {
            Swal.fire({
              title: '¡Email Verificado!',
              html: `
                <p>Tu cuenta ha sido verificada correctamente.</p>
                <p><strong>¡Bienvenido, ${response.data.user.displayName}!</strong></p>
              `,
              icon: 'success',
              confirmButtonColor: '#1E2A38',
              timer: 3000
            }).then(() => {
              // Redirigir al dashboard
              this.router.navigate([this.returnUrl]);
            });
          },
          error: (error) => {
            Swal.fire({
              title: 'Error',
              text: error.error?.message || 'Código inválido o expirado',
              icon: 'error',
              confirmButtonColor: '#dc3545'
            }).then(() => {
              // Volver a mostrar el modal de verificación
              this.showVerificationModal(email);
            });
          }
        });
      }
    });
  }

  /**
   * Reenviar código de verificación
   */
  private resendVerificationCode(email: string): void {
    // Deshabilitar botón temporalmente
    const resendBtn = document.getElementById('resend-btn');
    if (resendBtn) {
      resendBtn.innerHTML = '<span class="spinner-border spinner-border-sm"></span> Enviando...';
      resendBtn.style.pointerEvents = 'none';
    }

    this.authService.resendVerification(email).subscribe({
      next: () => {
        // Restaurar botón
        if (resendBtn) {
          resendBtn.innerHTML = '✓ Código enviado';
          setTimeout(() => {
            resendBtn.innerHTML = '¿No recibiste el código? Reenviar';
            resendBtn.style.pointerEvents = 'auto';
          }, 3000);
        }

        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: 'Código reenviado exitosamente',
          showConfirmButton: false,
          timer: 2500
        });
      },
      error: () => {
        // Restaurar botón en caso de error
        if (resendBtn) {
          resendBtn.innerHTML = '¿No recibiste el código? Reenviar';
          resendBtn.style.pointerEvents = 'auto';
        }

        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'error',
          title: 'Error al reenviar código',
          showConfirmButton: false,
          timer: 2000
        });
      }
    });
  }
}
